import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import {
  getDataInicio,
  fetchOSConcluidasNoPeriodo,
  fetchTotalOSNoPeriodo,
  fetchItensOSConcluidas,
  fetchFaturamentoRows,
  calcularMetricasFinanceiras,
  calcularServicosMaisVendidos,
  agruparFaturamentoPorDia,
  type Periodo,
  type MetricasFinanceiras,
  type ServicoMaisVendido,
  type FaturamentoPorPeriodo,
} from '@/services/dashboard-financeiro.service';

// Re-export types for backward compatibility with existing consumers
export type { MetricasFinanceiras, ServicoMaisVendido, FaturamentoPorPeriodo };

export function useMetricasFinanceiras(periodo: Periodo = '30d') {
  const { isAdmin } = useAuthContext();
  const { organizationId } = useOrganization();

  return useQuery<MetricasFinanceiras>({
    queryKey: ['dashboard-financeiro', organizationId, 'metricas', periodo, { isAdmin }],
    queryFn: async () => {
      const dataInicio = getDataInicio(periodo);
      const [osConcluidas, totalOS] = await Promise.all([
        fetchOSConcluidasNoPeriodo(dataInicio),
        fetchTotalOSNoPeriodo(dataInicio),
      ]);
      return calcularMetricasFinanceiras(osConcluidas, totalOS);
    },
    staleTime: 2 * 60 * 1000,
    enabled: isAdmin,
  });
}

export function useServicosMaisVendidos(periodo: Periodo = '30d', limite: number = 10) {
  const { isAdmin } = useAuthContext();
  const { organizationId } = useOrganization();

  return useQuery<ServicoMaisVendido[]>({
    queryKey: ['dashboard-financeiro', organizationId, 'servicos-mais-vendidos', periodo, limite, { isAdmin }],
    queryFn: async () => {
      const dataInicio = getDataInicio(periodo);
      const itens = await fetchItensOSConcluidas(dataInicio);
      return calcularServicosMaisVendidos(itens, limite);
    },
    staleTime: 2 * 60 * 1000,
    enabled: isAdmin,
  });
}

export function useFaturamentoPorPeriodo(periodo: Periodo = '30d') {
  const { isAdmin } = useAuthContext();
  const { organizationId } = useOrganization();

  return useQuery<FaturamentoPorPeriodo[]>({
    queryKey: ['dashboard-financeiro', organizationId, 'faturamento-periodo', periodo, { isAdmin }],
    queryFn: async () => {
      const dataInicio = getDataInicio(periodo);
      const ordens = await fetchFaturamentoRows(dataInicio);
      return agruparFaturamentoPorDia(ordens, periodo);
    },
    staleTime: 2 * 60 * 1000,
    enabled: isAdmin,
  });
}

// Re-export centralizado para manter compatibilidade de import
export { formatarMoeda as formatarValorEUR } from '@/lib/currency';
